// Partner streams (server/streams.ts): a "Live" chip on the boosts line while a partner is live, a
// small list of who is live, and a dock that plays the stream inside the game. While the dock is
// open, on screen, big enough and playing, it sends a heartbeat every STREAMS.beatMs; the server
// pays the watch bonus (+15% kill XP) while those are fresh and the partner is live. Nothing here
// shows until the server sends { t: 'streams' }, which it only does when the feature is on.
import type { ClientMsg } from '../shared/protocol.ts';
import type { LiveStream, StreamsDTO } from '../shared/streams.ts';
import { esc, full } from './fmt.ts';
import { placeBeside } from './tip.ts';
import { multX } from '../shared/boosts.ts';

let send: (m: ClientMsg) => void = () => {};
let rerender: () => void = () => {};
export function bindStreams(o: { send: typeof send; render: typeof rerender }) { send = o.send; rerender = o.render; }

let data: StreamsDTO | null = null;
/** The watch bonus in the boost lists' notation: "×1.15 XP". */
const bonusX = () => multX(data?.xpMult ?? 1.15);

export function onStreams(m: StreamsDTO) {
  data = { live: m.live ?? [], boost: m.boost ?? null, xpMult: m.xpMult, beatMs: m.beatMs };
  if (pop) renderPop();
  if (dock) renderDockHead();
  rerender();
}

/** The partner paying this player the watch bonus now (the dock must still be open). */
export function streamBoost(): string | null {
  return data?.boost && dock && dock.login === data.boost ? data.boost : null;
}
export const streamBoostX = bonusX;

const nameOf = (login: string) => data?.live.find((s) => s.login === login)?.name ?? login;

/** The chip for the boosts line: nothing while nobody is live and no bonus runs. */
export function liveChip(): string {
  if (!data) return '';
  const on = streamBoost();
  if (on) return `<button type="button" class="bchip lv-chip on" data-key="live" data-act="live-list" title="${esc(`Streamer: ${bonusX()} while you watch ${nameOf(on)}`)}"><i class="lv-dot"></i><b>Live</b><em>${bonusX()}</em></button>`;
  const n = data.live.length;
  if (!n) return '';
  return `<button type="button" class="bchip lv-chip" data-key="live" data-act="live-list" title="${esc(`Watch here for ${bonusX()}`)}"><i class="lv-dot"></i><b>Live</b><em>${n}</em></button>`;
}

/** ui.ts routes data-act="live-…" clicks here. */
export function streamsClick(el: HTMLElement): boolean {
  if (el.dataset.act !== 'live-list') return false;
  if (pop) closePop(); else openPop(el);
  return true;
}

// ---------------- the live list ----------------
let pop: HTMLElement | null = null;

function viewers(n: number) { return n >= 10_000 ? `${(n / 1000).toFixed(0)}k` : full(n); }

function row(s: LiveStream) {
  const watching = dock?.login === s.login;
  return `<button type="button" class="lv-row${watching ? ' on' : ''}" data-lv-open="${esc(s.login)}">
    <span class="lv-th">${s.thumb ? `<img src="${esc(s.thumb)}" alt="" loading="lazy" referrerpolicy="no-referrer">` : ''}</span>
    <span class="lv-tx"><span class="lv-nm"><b>${esc(s.name)}</b><span class="lv-v"><i class="lv-dot"></i>${viewers(s.viewers)}</span></span>
      <span class="lv-tt">${esc(s.title || s.game)}</span>${s.game && s.title ? `<span class="lv-gm">${esc(s.game)}</span>` : ''}</span></button>`;
}

function renderPop() {
  if (!pop) return;
  const live = data?.live ?? [];
  if (!live.length) { closePop(); return; }
  pop.innerHTML = `<div class="lv-hd"><b>Live now</b><span>${bonusX()} while you watch here</span></div>${live.map(row).join('')}`;
}

function openPop(anchor: HTMLElement) {
  if (!data?.live.length) return;
  pop = document.createElement('div');
  pop.className = 'panel lv-pop';
  pop.setAttribute('role', 'dialog');
  pop.setAttribute('aria-label', 'Live partner streams');
  document.body.append(pop);
  renderPop();
  pop.style.width = `${Math.min(340, innerWidth - 16)}px`;
  placeBeside(pop, anchor, { prefer: 'below', align: 'start' });
  pop.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest('[data-lv-open]') as HTMLElement | null;
    if (b) { openDock(b.dataset.lvOpen!); closePop(); }
  });
}

function closePop() { pop?.remove(); pop = null; }

document.addEventListener('click', (e) => {
  if (!pop) return;
  const t = e.target as HTMLElement;
  if (!pop.contains(t) && !t.closest('[data-act="live-list"]')) closePop();
});
document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closePop(); });

// ---------------- the dock ----------------
const PARENTS = ['legendkin.com', 'www.legendkin.com', 'mythic-idle.fly.dev', 'localhost'];
const MIN_W = 320, MIN_H = 180;
/** The player must show at least this much on screen to count (a little under the dock's smallest size). */
const SEEN_W = 300, SEEN_H = 168;
interface Dock { login: string; el: HTMLElement; head: HTMLElement; body: HTMLElement; playing: boolean; player: { isPaused?: () => boolean } | null; timer: number }
let dock: Dock | null = null;

type TwitchNs = { Embed: (new (el: HTMLElement, o: Record<string, unknown>) => { addEventListener(ev: string, fn: () => void): void; getPlayer(): { isPaused?: () => boolean; addEventListener?(ev: string, fn: () => void): void } }) & Record<string, string>; Player?: Record<string, string> };
let script: Promise<TwitchNs | null> | null = null;
function twitchScript(): Promise<TwitchNs | null> {
  return (script ??= new Promise((resolve) => {
    const w = window as unknown as { Twitch?: TwitchNs };
    if (w.Twitch?.Embed) return resolve(w.Twitch);
    const s = document.createElement('script');
    s.src = 'https://embed.twitch.tv/embed/v1.js';
    s.async = true;
    const done = () => resolve(w.Twitch?.Embed ? w.Twitch : null);
    s.onload = done;
    s.onerror = () => { script = null; resolve(null); };
    setTimeout(done, 8000);
    document.head.append(s);
  }));
}

function parents() { return [...new Set([...PARENTS, location.hostname])]; }

function saved(): { x: number; y: number; w: number; h: number } | null {
  try { const v = JSON.parse(localStorage.getItem('mi-lv-dock') ?? 'null'); return v && typeof v.w === 'number' ? v : null; } catch { return null; }
}
function save(el: HTMLElement) {
  const r = el.getBoundingClientRect();
  try { localStorage.setItem('mi-lv-dock', JSON.stringify({ x: Math.round(r.left), y: Math.round(r.top), w: Math.round(r.width), h: Math.round(r.height) })); } catch { /* storage off */ }
}

function place(el: HTMLElement) {
  const s = saved();
  // Phones: the smallest size, clear of the bottom tab bar.
  const narrow = innerWidth < 700, below = narrow ? 76 : 16;
  const w = Math.max(MIN_W + 8, Math.min(s?.w ?? (narrow ? MIN_W + 8 : 400), innerWidth - 32));
  const h = Math.max(MIN_H + 44, Math.min(s?.h ?? (narrow ? MIN_H + 44 : 255), innerHeight - 32));
  el.style.width = `${w}px`; el.style.height = `${h}px`;
  el.style.left = `${Math.max(16, Math.min(s?.x ?? 16, innerWidth - w - 16))}px`;
  el.style.top = `${Math.max(16, Math.min(s?.y ?? innerHeight - h - below, innerHeight - h - 16))}px`;
}

function renderDockHead() {
  if (!dock) return;
  const s = data?.live.find((x) => x.login === dock!.login);
  const on = streamBoost();
  dock.head.innerHTML = `<i class="lv-dot${s ? '' : ' off'}"></i><b>${esc(s?.name ?? dock.login)}</b>${s ? `<span class="lv-v">${viewers(s.viewers)}</span>` : '<span class="lv-v">Offline</span>'}
    ${on ? `<span class="lv-on" title="Trainer and creature kill XP">${bonusX()}</span>` : ''}
    <a class="lv-ext" href="https://www.twitch.tv/${encodeURIComponent(dock.login)}" target="_blank" rel="noopener" title="Open on Twitch" aria-label="Open on Twitch">↗</a>
    <button type="button" class="lv-x" data-lv-close title="Close" aria-label="Close stream">×</button>`;
}

/** Open, on screen at a usable size, the tab in front, and the player not paused. */
function watchable(d: Dock): boolean {
  if (document.visibilityState !== 'visible') return false;
  const r = d.body.getBoundingClientRect();
  const w = Math.min(r.right, innerWidth) - Math.max(r.left, 0), h = Math.min(r.bottom, innerHeight) - Math.max(r.top, 0);
  if (w < SEEN_W || h < SEEN_H) return false;
  try { if (d.player?.isPaused?.()) return false; } catch { /* the player is mid-load */ }
  return d.playing;
}

function beat() {
  if (dock && watchable(dock)) send({ t: 'streamBeat', login: dock.login });
}

export function openDock(login: string) {
  if (dock?.login === login) return;
  closeDock();
  const el = document.createElement('div');
  el.className = 'panel lv-dock';
  el.setAttribute('role', 'region');
  el.setAttribute('aria-label', 'Partner stream');
  el.innerHTML = '<div class="lv-dhd"></div><div class="lv-body"></div>';
  document.body.append(el);
  place(el);
  const d: Dock = { login, el, head: el.querySelector('.lv-dhd')!, body: el.querySelector('.lv-body')!, playing: false, player: null, timer: 0 };
  dock = d;
  renderDockHead();
  d.timer = window.setInterval(beat, data?.beatMs ?? 60_000);
  d.head.addEventListener('click', (e) => { if ((e.target as HTMLElement).closest('[data-lv-close]')) closeDock(); });
  drag(d);
  new ResizeObserver(() => { if (dock === d) save(el); }).observe(el);
  void twitchScript().then((T) => {
    if (dock !== d) return;
    const host = document.createElement('div');
    host.id = `lv-embed-${Date.now()}`;
    host.className = 'lv-embed';
    d.body.append(host);
    if (T) {
      const embed = new T.Embed(host, { width: '100%', height: '100%', channel: login, layout: 'video', autoplay: true, muted: true, parent: parents() });
      const playing = (on: boolean) => () => { if (dock !== d) return; const first = on && !d.playing; d.playing = on; if (first) beat(); };
      embed.addEventListener(T.Embed.VIDEO_PLAY ?? 'video.play', playing(true));
      embed.addEventListener(T.Embed.VIDEO_READY ?? 'video.ready', () => {
        const p = embed.getPlayer();
        d.player = p;
        const P = T.Player ?? {};
        p.addEventListener?.(P.PAUSE ?? 'pause', playing(false));
        p.addEventListener?.(P.PLAY ?? 'play', playing(true));
        p.addEventListener?.(P.PLAYING ?? 'playing', playing(true));
        p.addEventListener?.(P.ENDED ?? 'ended', playing(false));
        p.addEventListener?.(P.OFFLINE ?? 'offline', playing(false));
      });
    } else {
      // The embed script did not load: the plain player, counted while it is open and visible.
      const q = new URLSearchParams({ channel: login, muted: 'true', autoplay: 'true' });
      for (const p of parents()) q.append('parent', p);
      host.innerHTML = `<iframe src="https://player.twitch.tv/?${q}" title="Twitch stream" allow="autoplay; fullscreen" allowfullscreen></iframe>`;
      d.playing = true;
      setTimeout(beat, 5000);
    }
  });
  rerender();
}

export function closeDock() {
  if (!dock) return;
  clearInterval(dock.timer);
  dock.el.remove();
  dock = null;
  send({ t: 'streamStop' });
  rerender();
}

document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') beat(); });

/** Move the dock by its header; it stays on screen. */
function drag(d: Dock) {
  d.head.addEventListener('pointerdown', (e) => {
    if ((e.target as HTMLElement).closest('button, a')) return;
    const r = d.el.getBoundingClientRect(), ox = e.clientX - r.left, oy = e.clientY - r.top;
    d.head.setPointerCapture(e.pointerId);
    const move = (ev: PointerEvent) => {
      d.el.style.left = `${Math.max(0, Math.min(ev.clientX - ox, innerWidth - r.width))}px`;
      d.el.style.top = `${Math.max(0, Math.min(ev.clientY - oy, innerHeight - r.height))}px`;
    };
    const up = () => { d.head.removeEventListener('pointermove', move); d.head.removeEventListener('pointerup', up); save(d.el); };
    d.head.addEventListener('pointermove', move);
    d.head.addEventListener('pointerup', up);
  });
}
